import { NavLink, useNavigate } from "react-router-dom";
import { Cctv, CirclePlus, LogOut, Settings } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";

export const NAV_ITEMS = [
  { to: "/cameras", icon: Cctv, label: "Caméras", testid: "nav-cameras" },
  { to: "/cameras/new", icon: CirclePlus, label: "Ajouter", testid: "nav-add-camera" },
  { to: "/settings", icon: Settings, label: "Paramètres", testid: "nav-settings" },
];

function Tooltip({ children, visible }) {
  if (!visible) return null;
  return (
    <span className="pointer-events-none absolute left-[calc(100%+10px)] top-1/2 -translate-y-1/2 whitespace-nowrap rounded-lg border border-white/[0.08] bg-[#1b1b1f] px-2.5 py-1.5 text-xs font-medium text-zinc-200 opacity-0 shadow-xl shadow-black/30 transition-opacity group-hover:opacity-100 z-50">
      {children}
    </span>
  );
}

/** Shared desktop/mobile navigation content. */
export function SidebarContent({ collapsed = false, onNavigate }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const handleLogout = async () => {
    await logout();
    onNavigate?.();
    nav("/login", { replace: true });
  };

  return (
    <div className="h-full flex flex-col" data-testid="sidebar-content">
      <div
        className={cn(
          "h-16 border-b border-white/[0.06] flex items-center shrink-0",
          collapsed ? "justify-center" : "px-4 gap-3",
        )}
      >
        <div className="size-9 rounded-xl bg-brand/15 text-brand grid place-items-center shrink-0">
          <Cctv className="size-[18px]" strokeWidth={1.8} />
        </div>
        {!collapsed && <span className="text-base font-semibold tracking-tight">CAM</span>}
      </div>

      <nav className={cn("flex-1 py-4", collapsed ? "px-2" : "px-3")} aria-label="Navigation principale">
        <div className="space-y-2">
          {NAV_ITEMS.map((item) => (
            <div key={item.to} className="relative group">
              <NavLink
                to={item.to}
                data-testid={item.testid}
                onClick={() => onNavigate?.()}
                title={collapsed ? item.label : undefined}
                aria-label={collapsed ? item.label : undefined}
                className={({ isActive }) =>
                  cn(
                    "relative flex items-center transition-colors",
                    collapsed
                      ? "size-12 justify-center rounded-xl"
                      : "h-11 gap-3 rounded-xl px-3 text-sm font-medium",
                    isActive
                      ? "bg-white/[0.09] text-white"
                      : "text-zinc-400 hover:text-white hover:bg-white/[0.05]",
                  )
                }
              >
                <item.icon className="size-5 shrink-0" strokeWidth={1.7} />
                {!collapsed && <span>{item.label}</span>}
              </NavLink>
              <Tooltip visible={collapsed}>{item.label}</Tooltip>
            </div>
          ))}
        </div>
      </nav>

      <div className={cn("border-t border-white/[0.06] py-3", collapsed ? "px-2" : "px-3")}>
        {!collapsed && user && (
          <div className="px-3 pb-3 mb-1 border-b border-white/[0.06]" data-testid="sidebar-user-block">
            <div className="text-sm font-medium text-zinc-200 truncate">{user.name || "Administrateur"}</div>
            <div className="text-xs text-zinc-500 truncate mt-0.5" data-testid="sidebar-user-email">{user.email}</div>
          </div>
        )}

        <div className="relative group mt-1">
          <button
            onClick={handleLogout}
            data-testid="sidebar-logout"
            title={collapsed ? "Déconnexion" : undefined}
            aria-label={collapsed ? "Déconnexion" : undefined}
            className={cn(
              "w-full flex items-center text-zinc-500 hover:text-white hover:bg-white/[0.05] transition-colors",
              collapsed
                ? "size-12 justify-center rounded-xl"
                : "h-11 gap-3 rounded-xl px-3 text-sm font-medium",
            )}
          >
            <LogOut className="size-5 shrink-0" strokeWidth={1.7} />
            {!collapsed && <span>Déconnexion</span>}
          </button>
          <Tooltip visible={collapsed}>Déconnexion</Tooltip>
        </div>
      </div>
    </div>
  );
}
